import * as React from 'react';
import { Box, Stack, ThemeProvider, Typography } from '@mui/material';
import { generateTheme } from '../themes';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from "gsap";

const KeyFeaturesSlice = ({ data, index, direction }) => {
    // Support both Prismic data format and WordPress slice format
    let features = [];
    let colorTheme = 'white';
    let margins = 'normal';

    if (data?.domNode) {
        // WordPress format - extract data from DOM node
        const slice = data.domNode;
        colorTheme = slice.dataset.colorTheme || 'white';
        margins = slice.dataset.margins || 'normal';
        
        // Extract features from feature items
        const featureItems = slice.querySelectorAll('.feature-item');
        features = Array.from(featureItems).map(item => {
            const iconElement = item.querySelector('.feature-icon');
            const textElement = item.querySelector('.feature-text') || item.querySelector('h3');
            
            return {
                iconUrl: iconElement?.src || item.dataset.iconUrl || '',
                iconAlt: iconElement?.alt || item.dataset.iconAlt || '',
                text: textElement?.innerHTML || textElement?.textContent || ''
            };
        });
    } else if (data?.features) {
        // Prismic format - use existing data structure
        features = data.features || [];
        colorTheme = data.colorTheme || 'white';
        margins = data.margins || 'normal';
    }

    const containerRef = useRef(null);
    const theme = generateTheme(colorTheme);

    useLayoutEffect(() => {
        if (!containerRef.current) return;

        const links = gsap.utils.toArray(".keyFeature");

        links.forEach((a, index) => {
            gsap.fromTo(
                a,
                { opacity: 0, y: 10 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.4,
                    delay: 0.1 * index,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: a,
                        start: 'top 92%',
                        markers: false,
                    },
                }
            );
        });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box 
                ref={containerRef} 
                sx={{
                    display: "flex", 
                    alignItems: "flex-start", 
                    justifyContent: "flex-start", 
                    position: "relative",
                    background: (theme) => theme.palette.background.default,
                    px: { xs: 2, sm: 3, md: 5 }, 
                    py: { xs: "20vw", sm: "10vw", md: "6vw" }, 
                    minHeight: { xs: "100vw", sm: "100%", md: "100%" },
                }}
            >
                <Stack 
                    direction={{ xs: "column", md: "row" }} 
                    sx={{ 
                        width: "100%", 
                        alignItems: "center", 
                        justifyContent: "center", 
                        position: "relative", 
                        zIndex: 1 
                    }}
                >
                    {features.map((feature, index) => (
                        <Box 
                            key={index}
                            className="keyFeature" 
                            sx={{ 
                                width: { xs: "100%", sm: "100%", md: "100%" }, 
                                display: "flex", 
                                flexDirection: "column", 
                                alignItems: "center", 
                                justifyContent: "center", 
                                textAlign: "center", 
                                mb: { xs: 6, md: 0 } 
                            }}
                        >
                            <img 
                                src={feature.iconUrl} 
                                alt={feature.iconAlt || `Feature ${index + 1} icon`}
                                style={{ 
                                    width: "74px", 
                                    height: "auto", 
                                    marginBottom: "8px"
                                }} 
                            />

                            <Box sx={{ width: "100%", textAlign: "center" }}>
                                <Typography
                                    variant="h4"
                                    component="h2"
                                    dangerouslySetInnerHTML={{ __html: feature.text }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </ThemeProvider>
    );
};

export default KeyFeaturesSlice;